<script setup lang="ts">
import { computed, inject } from 'vue';
import { useChatStore, type UiToolBlock } from '@/stores/chat';
import { CHAT_ASK } from '@/utils/chatTools';

/**
 * Aanbevelingen van de assistent (tool `suggest_actions`) met per aanbeveling een knop "Uitvoeren".
 * Een klik stuurt de instructie als gebruikersbericht; de assistent voert hem dan uit met de
 * schrijftools (concept in het formulier of bevestigingskaart, zoals altijd).
 */
const props = withDefaults(
  defineProps<{
    block: UiToolBlock;
    /** Alleen bij de meest recente aanbevelingen zijn de knoppen bruikbaar */
    actionable?: boolean;
  }>(),
  { actionable: false },
);

interface SuggestedAction {
  label: string;
  instruction: string;
}

const chat = useChatStore();
const ask = inject(CHAT_ASK, null);

const actions = computed<SuggestedAction[]>(() => {
  const list = (props.block.input as { actions?: unknown } | null)?.actions;
  if (!Array.isArray(list)) return [];
  return list
    .filter(
      (a): a is SuggestedAction =>
        !!a && typeof a.label === 'string' && typeof a.instruction === 'string' && !!a.instruction.trim(),
    )
    .map((a) => ({ label: a.label, instruction: a.instruction }));
});

/** Al gevraagde aanbevelingen (in de chat-store, zodat het na een nieuwe render blijft staan) */
const sent = computed(
  () => new Set(actions.value.map((_, i) => i).filter((i) => chat.isActionSent(props.block.id, i))),
);

const canRun = computed(() => props.actionable && !!ask && !chat.streaming);
const allSent = computed(() => actions.value.length > 0 && sent.value.size === actions.value.length);

function run(index: number) {
  if (!canRun.value || sent.value.has(index)) return;
  chat.markActionSent(props.block.id, index);
  void ask!(`Voer deze aanbeveling uit: ${actions.value[index].instruction}`);
}

function runAll() {
  if (!canRun.value) return;
  const todo = actions.value.map((a, i) => ({ a, i })).filter(({ i }) => !sent.value.has(i));
  if (!todo.length) return;
  for (const { i } of todo) chat.markActionSent(props.block.id, i);
  const text =
    todo.length === 1
      ? `Voer deze aanbeveling uit: ${todo[0].a.instruction}`
      : `Voer deze aanbevelingen uit:\n${todo.map(({ a }, n) => `${n + 1}. ${a.instruction}`).join('\n')}`;
  void ask!(text);
}
</script>

<template>
  <div v-if="actions.length" class="kl-chat-actions" data-testid="chat-suggested-actions">
    <div class="kl-chat-actions-head small fw-semibold">
      <i class="bi bi-lightbulb me-1" aria-hidden="true"></i>Aanbevelingen
    </div>
    <ul class="list-unstyled mb-0">
      <li
        v-for="(a, i) in actions"
        :key="i"
        class="kl-chat-action d-flex align-items-start gap-2"
        data-testid="chat-suggested-action"
      >
        <span class="flex-grow-1 small">{{ a.label }}</span>
        <button
          type="button"
          class="btn btn-sm py-0 flex-shrink-0"
          :class="sent.has(i) ? 'btn-outline-success' : 'btn-outline-primary'"
          :disabled="!canRun || sent.has(i)"
          :title="a.instruction"
          data-testid="chat-action-run"
          @click="run(i)"
        >
          <template v-if="sent.has(i)"><i class="bi bi-check2 me-1" aria-hidden="true"></i>Gevraagd</template>
          <template v-else><i class="bi bi-play-fill me-1" aria-hidden="true"></i>Uitvoeren</template>
        </button>
      </li>
    </ul>
    <div v-if="actions.length > 1" class="text-end mt-1">
      <button
        type="button"
        class="btn btn-sm btn-primary py-0"
        :disabled="!canRun || allSent"
        data-testid="chat-action-run-all"
        @click="runAll"
      >
        <i class="bi bi-play-fill me-1" aria-hidden="true"></i>Alles uitvoeren
      </button>
    </div>
  </div>
</template>

<style scoped>
.kl-chat-actions {
  border: 1px solid var(--bs-primary-border-subtle);
  background: var(--bs-primary-bg-subtle);
  border-radius: 0.5rem;
  padding: 0.4rem 0.6rem;
  margin: 0.35rem 0;
}
.kl-chat-action + .kl-chat-action {
  border-top: 1px solid var(--bs-border-color-translucent);
}
.kl-chat-action {
  padding: 0.3rem 0;
}
</style>
